import {forwardRef} from '@chakra-ui/react';
import type {GroupBase, Props, SelectInstance} from 'chakra-react-select';
import type {ReactElement, RefAttributes} from 'react';
import React from 'react';

import {useAvatarSelectStyleConfig} from '../../../theme-config';
import {ReactSelect} from '../ReactSelect';
import type {AvatarOptionNew} from '../schema';
import type {SelectRef} from '../types';

import {LoadingIndicator} from './LoadingIndicator';
import {AvatarReactSelectProvider} from './context';
import {formatOptionLabel} from './formatOptionLabel';

export type AvatarReactSelectComponentProps<
  Option extends AvatarOptionNew,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Props<Option, IsMulti, Group> & RefAttributes<SelectInstance<Option, IsMulti, Group>>;

export type AvatarReactSelectComponent = <
  Option extends AvatarOptionNew,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: AvatarReactSelectComponentProps<Option, IsMulti, Group>,
) => ReactElement<any>;

export const AvatarReactSelect = forwardRef(
  <Option extends AvatarOptionNew, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: Props<Option, IsMulti, Group>,
    ref: SelectRef<Option, IsMulti, Group>,
  ) => {
    const styles = useAvatarSelectStyleConfig(props);
    const {formatOptionLabel: formatOptionLabelProp, ...rest} = props;

    return (
      <AvatarReactSelectProvider value={{size: props.size}}>
        <ReactSelect
          chakraStyles={{
            indicatorSeparator: provided => ({...provided, ...styles.indicatorSeparator}),
            indicatorsContainer: provided => ({...provided, ...styles.indicatorsContainer}),
            dropdownIndicator: provided => ({...provided, ...styles.dropdownIndicator}),
            control: provided => ({...provided, ...styles.control}),
            downChevron: provided => ({...provided, ...styles.downChevron}),
            valueContainer: (provided, state) => ({
              ...provided,
              ...styles.valueContainer,
              ...(state.selectProps.isInitialLoading && !state.hasValue && {display: `none`}),
            }),
          }}
          styles={{
            menuPortal: base => ({...base, zIndex: 9999}),
            menu: base => ({
              ...base,
              marginTop: `-10px`,
              zIndex: 9999,
            }),
          }}
          formatOptionLabel={formatOptionLabelProp || formatOptionLabel}
          {...rest}
          components={{
            LoadingIndicator,
            ...props.components,
          }}
          ref={ref}
        />
      </AvatarReactSelectProvider>
    );
  },
) as AvatarReactSelectComponent;
